import { useCallback, useState, useEffect } from "react";
import axios from "../api/axiosInstance";
import { formatDistanceToNow } from "date-fns";
import Cookies from "js-cookie";
import { FaTrash } from "react-icons/fa";
import useAuthStore from "../store/useAuthStore";

const CommentSection = ({ blogId }) => {
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const token = Cookies.get("token");

  const { userId, role } = useAuthStore();

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`/blog/${blogId}/comments`);
      setComments(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [blogId]);

  const postComment = async () => {
    if (!token) {
      setShowModal(true);
      return;
    }
    if (!comment.trim()) return;
    try {
      await axios.post(
        `/blog/${blogId}/comments`,
        { blogId, content: comment },
        { withCredentials: true }
      );
      setComment("");
      fetchComments();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.details?.[0]?.message || err.response?.data?.error || "Failed to post comment.");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await axios.delete(
        `/blog/${blogId}/comments/${commentId}`,
        { withCredentials: true }
      );
      fetchComments();
    } catch (err) {
      console.error(err);
      alert("Failed to delete comment: " + (err.response?.data?.message || err.message));
    }
  };

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return (
    <>
      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-2">Comments</h3>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md text-base resize-y text-black focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          placeholder="Write a comment..."
        />
        <button 
          onClick={postComment} 
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 mt-2 border-0 rounded-md cursor-pointer font-medium transition-colors"
        >
          Post
        </button>

        {loading ? (
          <p className="mt-4 text-[0.95rem]">Loading...</p>
        ) : (
          comments
            .slice() // create a shallow copy to avoid mutating original
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // newest first
            .map((c) => {
              const isCommentAuthor = c.author?._id === userId;
              const isCommentAdmin = role === 'admin';
              const canDelete = isCommentAuthor || isCommentAdmin;

              return (
                <div key={c._id} className="relative group flex justify-between items-start border-b border-white/10 pb-4 mt-4">
                  <div className="flex gap-2 w-full">
                    <div className="w-10 h-10 bg-blue-50 text-blue-700 font-bold rounded-full mt-3.5 flex items-center justify-center flex-shrink-0">
                      {(c.author?.name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 ml-3">
                      <p className="font-semibold text-blue-400 mb-1">{c.author?.name || 'Anonymous'}</p>
                      <p className="text-[#f0f0f2] m-0">{c.content}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatDistanceToNow(new Date(c.createdAt))} ago
                      </p>
                    </div>
                  </div>
                  {canDelete && (
                    <button
                      onClick={() => handleDeleteComment(c._id)}
                      className="text-red-500 hover:text-red-600 transition p-1.5 rounded hover:bg-red-500/10 self-center md:opacity-0 md:group-hover:opacity-100 focus:opacity-100"
                      title="Delete comment"
                    >
                      <FaTrash size={14} />
                    </button>
                  )}
                </div>
              );
            })
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000]">
          <div className="bg-white p-8 rounded-xl text-center shadow-2xl w-[300px] border border-gray-100">
            <h2 className="text-xl font-bold mb-4 text-black">You need to login first to write a comment</h2>
            <div className="flex justify-around mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded-md font-bold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  window.location.href = "/login";
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-bold transition-colors"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommentSection;
